import { useState } from "react";
import { toast } from "sonner";
import {
  Container, Section, PageHero, CtaBanner, ArrowRight, Star, CheckCircle2,
  GOOGLE_REVIEW_URL, PHONE,
} from "../components/common";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitFeedback } from "../lib/api";
import { useSeo, breadcrumbLd, ORIGIN } from "../lib/seo";

const HERO = "https://images.unsplash.com/photo-1676018366904-c083ed678e60?w=1400&q=80";

function StarPicker({ rating, setRating }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex justify-center gap-2" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          data-testid={`review-star-${i}`}
          aria-label={`${i} star${i > 1 ? "s" : ""}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(0)}
          onClick={() => setRating(i)}
          className="transition-transform hover:scale-110"
        >
          <Star
            size={44}
            className={(hover || rating) >= i ? "fill-primary text-primary" : "text-white/25"}
          />
        </button>
      ))}
    </div>
  );
}

export default function Review() {
  const [rating, setRating] = useState(0);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Leave a Review | RPM Auto Repair, Riverside CA",
    description: "How did we do? Share your experience with RPM Auto Repair in Riverside. Rate our ASE-certified mechanics and leave a review on Google.",
    path: "/review",
    jsonLd: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Leave a Review", path: "/review" }])],
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitFeedback({ ...form, rating });
      setDone(true);
      toast.success("Thank you, your feedback has been sent to our team.");
    } catch {
      toast.error("Something went wrong. Please call " + PHONE);
    } finally {
      setSending(false);
    }
  };

  const high = rating >= 4;

  return (
    <>
      <PageHero
        eyebrow="Your Opinion Matters"
        title="Leave Us a Review"
        subtitle="We're always working to earn your trust. Tell us how we did, it only takes a moment."
        image={HERO}
        crumbs={[{ name: "Home", path: "/" }, { name: "Leave a Review", path: "/review" }]}
      />
      <Section>
        <Container className="max-w-2xl">
          <div className="rounded-2xl border border-white/10 bg-steel p-8 sm:p-10" data-testid="review-card">
            {!rating && (
              <div className="text-center">
                <h2 className="font-display text-2xl text-white sm:text-3xl">How was your experience?</h2>
                <p className="mt-3 text-muted-foreground">Tap the stars to rate your visit with RPM Auto Repair.</p>
                <div className="mt-8"><StarPicker rating={rating} setRating={setRating} /></div>
              </div>
            )}

            {rating > 0 && high && (
              <div className="text-center" data-testid="review-high">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/15 text-primary">
                  <CheckCircle2 size={30} />
                </div>
                <h2 className="mt-5 font-display text-2xl text-white sm:text-3xl">Thank you, that means a lot!</h2>
                <p className="mt-3 text-muted-foreground">
                  We'd be grateful if you shared your {rating}-star experience where it helps other drivers find us. It takes about 30 seconds.
                </p>
                <div className="mt-8 flex justify-center">
                  <a
                    href={GOOGLE_REVIEW_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="review-google-btn"
                    className="flex items-center justify-center gap-2 rounded-md bg-primary px-8 py-4 font-semibold tracking-wide text-white transition-colors hover:bg-racing-dark"
                  >
                    Review us on Google <ArrowRight size={16} />
                  </a>
                </div>
                <button onClick={() => setRating(0)} className="mt-6 text-sm text-muted-foreground hover:text-white" data-testid="review-reset">
                  ← Change rating
                </button>
              </div>
            )}

            {rating > 0 && !high && !done && (
              <div data-testid="review-low">
                <div className="text-center">
                  <h2 className="font-display text-2xl text-white sm:text-3xl">We're sorry we fell short.</h2>
                  <p className="mt-3 text-muted-foreground">
                    Your honest feedback helps us make it right. Tell us what happened and our team will personally follow up.
                  </p>
                </div>
                <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="feedback-form">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Name</Label><Input value={form.name} onChange={set("name")} data-testid="feedback-name" className="border-white/15 bg-ink text-white" /></div>
                    <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="feedback-phone" className="border-white/15 bg-ink text-white" /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">Email</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="feedback-email" className="border-white/15 bg-ink text-white" /></div>
                  <div className="space-y-1.5"><Label className="text-sm text-muted-foreground">What could we have done better? *</Label><Textarea required rows={5} value={form.message} onChange={set("message")} data-testid="feedback-message" className="border-white/15 bg-ink text-white" /></div>
                  <button type="submit" disabled={sending} data-testid="feedback-submit" className="flex w-full items-center justify-center gap-2 rounded-md bg-primary py-4 font-semibold tracking-wide text-white hover:bg-racing-dark disabled:opacity-60">
                    {sending ? "Sending…" : <>Send Private Feedback <ArrowRight size={16} /></>}
                  </button>
                </form>
                <p className="mt-6 text-center text-sm text-muted-foreground">
                  Prefer to leave a public review? You can do that on{" "}
                  <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google</a>.
                </p>
                <button onClick={() => setRating(0)} className="mt-4 block w-full text-center text-sm text-muted-foreground hover:text-white" data-testid="review-reset">
                  ← Change rating
                </button>
              </div>
            )}

            {done && (
              <div className="text-center" data-testid="review-thanks">
                <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-primary/15 text-primary">
                  <CheckCircle2 size={30} />
                </div>
                <h2 className="mt-5 font-display text-2xl text-white sm:text-3xl">Thank you for telling us.</h2>
                <p className="mt-3 text-muted-foreground">
                  Our team has received your feedback and will follow up personally. We appreciate the chance to make things right.
                </p>
              </div>
            )}
          </div>
        </Container>
      </Section>
      <CtaBanner />
    </>
  );
}
